import React, { useMemo, useState } from 'react'
import ReactModal from 'react-modal'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useMutation } from '@apollo/client'
import { AUTH_TOKEN } from '../../../../constants'
import { IMPORT_PREFILL_RECIPIENTS } from '../../../../graphql/mutation'
import PrefillFieldMappingTable, { FieldMappingEntry } from './PrefillFieldMappingTable'
import { ExcelFormField } from './PrefillDistributionPanel'
import {
  detectDeliveryEmailFieldId,
  detectDeliveryNameFieldId,
  findMatchingExcelColumn,
  findMatchingFormField,
  getEmailFormField,
  PREFILL_MAX_FILE_BYTES,
  PREFILL_MAX_IMPORT_ROWS,
  resolveExcelFieldEditable,
  validateRowEmails
} from './prefillFieldUtils'

interface Props {
  isOpen: boolean
  onClose: () => void
  formUuid: string
  excelFormFields: ExcelFormField[]
  onImported: () => void
}

type Step = 'upload' | 'mapping' | 'importing' | 'errors'

const buildDefaultMapping = (
  columns: string[],
  excelFormFields: ExcelFormField[]
): FieldMappingEntry[] =>
  columns
    .map((col) => {
      const matched = findMatchingFormField(col, excelFormFields)
      if (!matched) return null
      return {
        excelColumn: col,
        formFieldId: matched.fieldId,
        formFieldLabel: matched.label,
        formFieldType: matched.type,
        prefillFromExcel: true,
        editable: resolveExcelFieldEditable(matched),
        editableOnCondition: null,
        isEditField: matched.isEditField,
        parentFieldId: matched.parentFieldId ?? undefined,
        isTableCell: matched.isTableCell,
        tableRowIndex: matched.tableRowIndex ?? undefined,
        tableColIndex: matched.tableColIndex ?? undefined,
        useAsRecipientName: matched.useAsRecipientName === true
      }
    })
    .filter(Boolean) as FieldMappingEntry[]

const PrefillExcelUploadModal: React.FC<Props> = ({
  isOpen,
  onClose,
  formUuid,
  excelFormFields,
  onImported
}) => {
  const [step, setStep] = useState<Step>('upload')
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [fieldMapping, setFieldMapping] = useState<FieldMappingEntry[]>([])
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [missingEmailColumn, setMissingEmailColumn] = useState(false)

  const emailFormField = useMemo(() => getEmailFormField(excelFormFields), [excelFormFields])

  const [importRecipients] = useMutation(IMPORT_PREFILL_RECIPIENTS, {
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } }
  })

  const reset = () => {
    setStep('upload')
    setFileName('')
    setRows([])
    setFieldMapping([])
    setImportErrors([])
    setMissingEmailColumn(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > PREFILL_MAX_FILE_BYTES) {
      toast.error(`File must be under ${PREFILL_MAX_FILE_BYTES / (1024 * 1024)}MB`)
      return
    }

    setLoading(true)
    setImportErrors([])
    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1]
        const token = localStorage.getItem(AUTH_TOKEN)
        const res = await axios.post(
          `${process.env.REACT_APP_RESUME_PARSER_URL}/form-builder/${formUuid}/prefill-parse`,
          { fileBase64: base64, fileName: file.name },
          { headers: { Authorization: `Bearer ${token}` } }
        )

        if (!res.data.success) {
          toast.error(res.data.message || 'Failed to parse file')
          return
        }

        const mapping = buildDefaultMapping(res.data.columns, excelFormFields)
        if (mapping.length === 0 && excelFormFields.length > 0) {
          toast.warning(
            'No matching form field columns found in the uploaded file. Use the downloaded template.'
          )
        }

        const emailColumnMissing =
          !!emailFormField && !findMatchingExcelColumn(emailFormField.label, res.data.columns)
        setMissingEmailColumn(emailColumnMissing)

        if (res.data.rows.length > PREFILL_MAX_IMPORT_ROWS) {
          toast.error(`Maximum ${PREFILL_MAX_IMPORT_ROWS} rows allowed per import`)
          return
        }

        setFileName(file.name)
        setRows(res.data.rows)
        setFieldMapping(mapping)
        setStep('mapping')

        if (emailColumnMissing) {
          toast.warning(
            `The required "${
              emailFormField!.label
            }" column is missing. Download the template from Step 1 and add it to your Excel file.`
          )
        } else {
          toast.success(`Parsed ${res.data.rows.length} row(s)`)
        }
      }
      reader.readAsDataURL(file)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to parse Excel file')
    } finally {
      setLoading(false)
      e.target.value = ''
    }
  }

  const handleImport = async () => {
    const deliveryEmailFieldId = detectDeliveryEmailFieldId(
      fieldMapping,
      undefined,
      excelFormFields
    )
    const deliveryNameFieldId = detectDeliveryNameFieldId(
      fieldMapping,
      deliveryEmailFieldId,
      undefined
    )

    if (!deliveryEmailFieldId || !emailFormField) {
      toast.error(
        'Add an Email input field (or a field with "email" in the label) to the form, save the form, then return here.'
      )
      return
    }

    const emailColumnEntry = fieldMapping.find((f) => f.formFieldId === deliveryEmailFieldId)

    if (!emailColumnEntry) {
      setImportErrors([
        `The required "${emailFormField.label}" column is missing from your Excel file.`,
        'Download the template from Step 1, add the column, enter a valid email address on each row, and upload again.'
      ])
      setStep('errors')
      return
    }

    const rowErrors = validateRowEmails(rows, emailColumnEntry.excelColumn)
    if (rowErrors.length > 0) {
      setImportErrors(rowErrors)
      setStep('errors')
      return
    }

    setStep('importing')
    setLoading(true)
    try {
      const { data } = await importRecipients({
        variables: {
          input: {
            formUuid,
            fileName,
            rows,
            fieldMapping: {
              deliveryEmailFieldId,
              deliveryNameFieldId,
              fields: fieldMapping.filter((f) => f.formFieldId)
            }
          }
        }
      })

      const result = data?.importPrefillRecipients
      if (result?.imported === 0) {
        const errorLines = [
          ...(result?.errors || []),
          ...(result?.warnings?.length ? ['', 'Notes:', ...(result.warnings as string[])] : [])
        ]
        setImportErrors(
          errorLines.length > 0
            ? errorLines
            : ['No recipients were imported. Check your Excel file and try again.']
        )
        setStep('errors')
        return
      }

      if (result?.warnings?.length) {
        toast.info(
          `${result.message || `Imported ${result.imported} recipient(s)`} (${
            result.warnings.length
          } duplicate row(s) ignored)`
        )
      } else if (result?.errors?.length) {
        toast.warning(result.message || `Imported ${result.imported} with warnings`)
      } else {
        toast.success(result?.message || 'Recipients imported')
      }
      onImported()
      handleClose()
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message ||
        err?.message ||
        'Import failed'
      setImportErrors([message])
      setStep('errors')
    } finally {
      setLoading(false)
    }
  }

  const emailColumnLabel = emailFormField?.label || 'Email ID'
  const showsEmailHelp =
    importErrors.some(
      (error) =>
        /missing value/i.test(error) ||
        /invalid email/i.test(error) ||
        /missing required column/i.test(error)
    ) || missingEmailColumn

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
      style={{
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          width: '700px',
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: '12px',
          padding: '1.5rem'
        }
      }}
    >
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h5 className='mb-0'>Step 2 — Upload Filled Excel</h5>
        <button type='button' className='btn-close' onClick={handleClose} />
      </div>

      {step === 'upload' && (
        <div>
          <p className='text-muted small'>
            Upload the filled template. Columns must match the form fields from Step 1.
            {emailFormField && (
              <>
                {' '}
                Every row must include a value in the <strong>{emailColumnLabel}</strong> column.
              </>
            )}
          </p>
          {!emailFormField && (
            <div className='alert alert-warning small'>
              This form has no email field. Add an <strong>Email input</strong> field (or a field
              with &quot;email&quot; in the label) in the form builder, save, then return here.
            </div>
          )}
          <input
            type='file'
            className='form-control'
            accept='.xlsx,.xls'
            onChange={handleFileChange}
            disabled={loading}
          />
          {loading && <p className='mt-2 text-muted'>Parsing file...</p>}
        </div>
      )}

      {step === 'mapping' && (
        <div>
          <p className='text-muted small mb-2'>
            {rows.length} row(s) from <strong>{fileName}</strong>. Review the column mapping below.
            Prefill and edit permissions are set in the form builder.
          </p>

          {missingEmailColumn && emailFormField && (
            <div className='alert alert-danger'>
              <strong>Missing required column:</strong> <code>{emailFormField.label}</code>
              <p className='mb-0 mt-2 small'>
                Download the Excel template from Step 1, add the{' '}
                <strong>{emailFormField.label}</strong> column, enter a valid email on each row, and
                upload the corrected file.
              </p>
            </div>
          )}

          {emailFormField && !missingEmailColumn && (
            <div className='alert alert-info py-2 small mb-3'>
              <i className='bi bi-envelope me-1' />
              The <strong>{emailFormField.label}</strong> column is required on every row — it is
              used to send form links to recipients.
            </div>
          )}

          {fieldMapping.length === 0 ? (
            <div className='alert alert-warning'>
              No form field columns from the template were found in this file. Download the template
              from Step 1 and fill that file.
            </div>
          ) : (
            <PrefillFieldMappingTable mapping={fieldMapping} />
          )}
          <div className='d-flex justify-content-end gap-2 mt-3'>
            <button type='button' className='btn btn-light' onClick={() => setStep('upload')}>
              Back
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleImport}
              disabled={loading || fieldMapping.length === 0 || missingEmailColumn}
            >
              Import Recipients
            </button>
          </div>
        </div>
      )}

      {step === 'importing' && <p className='text-muted'>Importing recipients...</p>}

      {step === 'errors' && (
        <div>
          <div className='alert alert-danger'>
            <strong>Could not import recipients.</strong>
            {showsEmailHelp && (
              <p className='mb-0 mt-2 small'>
                Every row needs a valid email in the <strong>{emailColumnLabel}</strong> column.
                Open your Excel file, fill in the missing values, and upload again.
              </p>
            )}
          </div>

          <ul className='list-group mb-3'>
            {importErrors.map((err, index) => (
              <li key={index} className='list-group-item list-group-item-danger small py-2'>
                {err}
              </li>
            ))}
          </ul>

          <div className='alert alert-light border small mb-0'>
            <strong>How to fix:</strong>
            <ol className='mb-0 ps-3 mt-2'>
              <li>
                Open <strong>{fileName || 'your Excel file'}</strong>
              </li>
              <li>
                Add or fill the <strong>{emailColumnLabel}</strong> column for each row
              </li>
              <li>Save the file and upload it again</li>
            </ol>
          </div>

          <div className='d-flex justify-content-end gap-2 mt-3'>
            <button type='button' className='btn btn-light' onClick={() => setStep('mapping')}>
              Back to mapping
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => {
                setImportErrors([])
                setStep('upload')
              }}
            >
              Upload corrected Excel
            </button>
          </div>
        </div>
      )}
    </ReactModal>
  )
}

export default PrefillExcelUploadModal
